import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  CheckCircle,
  GlobeHemisphereWest,
  ShieldCheck,
  Sparkle,
  UsersThree,
} from "@phosphor-icons/react";
import Logo from "@/components/Logo";
import UnifiedHeader from "@/components/UnifiedHeader";
import { STORE_LINKS } from "@/config/storeLinks";
import appStoreBadge from "@/assets/store/badge-app-store-de.svg";
import googlePlayBadge from "@/assets/store/badge-google-play-de.svg";

const FEATURES = [
  {
    icon: Sparkle,
    label: "Mit Lyo planen",
    title: "Aus einer Idee wird deine Reise",
    text: "Lyo hilft dir dabei, Ziele, Zeitraum, Budget und Reisewünsche Schritt für Schritt zu einer passenden Reise zusammenzuführen.",
    href: "/ki-reiseplaner",
    action: "Lyo kennenlernen",
  },
  {
    icon: UsersThree,
    label: "Reisepartner finden",
    title: "Menschen, die ähnlich reisen",
    text: "Finde passende Reisebegleitungen nach Reiseziel, Zeitraum, Interessen und persönlichem Reisestil.",
    href: "/reisepartner-finden",
    action: "Matching entdecken",
  },
  {
    icon: GlobeHemisphereWest,
    label: "Inspiration entdecken",
    title: "Nicht mit einer leeren Seite starten",
    text: "Entdecke konkrete Reiseideen und nutze sie direkt als Ausgangspunkt für deine eigene Planung.",
    href: "/reiseideen",
    action: "Reiseideen ansehen",
  },
];

const IDEAS = [
  {
    title: "Nordlichter in Norwegen",
    text: "Natur, Winter und besondere Momente",
    image: "/assets/travel-motif-fjord.perf-9161be7e222f.webp",
  },
  {
    title: "Gemeinsam ans Mittelmeer",
    text: "Sonne, Entspannung und kleine Abenteuer",
    image: "/assets/travel-collage-mobile.perf-3caa488eda07.webp",
  },
  {
    title: "Deine Reise mit Lyo",
    text: "Individuell geplant statt beliebig zusammengestellt",
    image: "/assets/voyalyo-travel-bg.perf-4a5fc1980ecb.webp",
  },
];

const STEPS = [
  {
    number: "01",
    title: "Inspirieren lassen",
    text: "Entdecke Ziele und Reiseideen, ohne dich zuerst registrieren zu müssen.",
  },
  {
    number: "02",
    title: "Reise planen",
    text: "Nutze Lyo, um deine Vorstellungen in einen konkreten Reiseplan zu verwandeln.",
  },
  {
    number: "03",
    title: "Passende Menschen finden",
    text: "Registriere dich erst dann, wenn du Kontakte und geschützte Funktionen nutzen möchtest.",
  },
];

function LyoDemoChat() {
  const [phase, setPhase] = useState(0);

  useEffect(() => {
    const timers = [
      window.setTimeout(() => setPhase(1), 700),
      window.setTimeout(() => setPhase(2), 2400),
      window.setTimeout(() => setPhase(3), 4600),
      window.setTimeout(() => setPhase(4), 7000),
      window.setTimeout(() => setPhase(5), 8600),
      window.setTimeout(() => setPhase(6), 10800),
      window.setTimeout(() => setPhase(7), 12400),
    ];

    return () => timers.forEach(window.clearTimeout);
  }, []);

  const TypingBubble = () => (
    <div className="absolute z-10 flex items-end gap-3 pointer-events-none">
      <img width="192" height="288" decoding="async"
        src="/assets/lyo.perf-511a1be44980.webp"
        alt=""
        className="h-9 w-9 flex-none object-contain"
      />
      <div className="rounded-2xl rounded-bl-md border border-white/10 bg-white/[.07] px-4 py-3">
        <div className="flex items-center gap-1.5">
          {[0, 1, 2].map((dot) => (
            <span
              key={dot}
              className="h-2 w-2 rounded-full bg-[#70e0df]"
              style={{
                animation: `lyoTyping 1.15s ${dot * 0.18}s ease-in-out infinite`,
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div className="overflow-hidden rounded-[30px] border border-white/15 bg-[#0a1524]/94 shadow-2xl shadow-black/45 backdrop-blur-xl">
      <div className="flex items-center justify-between border-b border-white/10 bg-white/[.04] px-5 py-4">
        <div className="flex items-center gap-3">
          <div className="relative">
            <img width="192" height="288" decoding="async"
              src="/assets/lyo.perf-511a1be44980.webp"
              alt="Lyo"
              className="h-12 w-12 object-contain"
            />
            <span className="absolute bottom-0 right-0 h-3.5 w-3.5 rounded-full border-2 border-[#0a1524] bg-emerald-400" />
          </div>
          <div>
            <p className="font-heading font-extrabold text-white">
              Mit Lyo planen
            </p>
            <p className="text-xs text-white/55">
              Dein persönlicher Reiseassistent
            </p>
          </div>
        </div>

        <span className="rounded-full border border-[#35c8c8]/30 bg-[#35c8c8]/10 px-3 py-1 text-[11px] font-extrabold uppercase tracking-wider text-[#70e0df]">
          Demo
        </span>
      </div>

      <div className="relative min-h-[545px] space-y-4 p-5 sm:p-6">
        <div
          className={`flex justify-end transition-all duration-700 ${
            phase >= 1 ? "translate-y-0 opacity-100" : "translate-y-3 opacity-0"
          }`}
        >
          <div className="max-w-[88%] rounded-2xl rounded-br-md bg-[#35c8c8] px-4 py-3 text-sm font-medium leading-relaxed text-[#07101d] shadow-lg">
            Ich möchte im Oktober eine Woche ans Meer. Nicht zu heiß und gerne
            mit ein paar Ausflügen. Wohin könnte ich reisen?
          </div>
        </div>

        {phase >= 2 && phase < 3 && <TypingBubble />}

        <div
          className={`flex items-end gap-3 transition-all duration-700 ${
            phase >= 3 ? "translate-y-0 opacity-100" : "translate-y-3 opacity-0"
          }`}
        >
          <img width="192" height="288" decoding="async"
            src="/assets/lyo.perf-511a1be44980.webp"
            alt=""
            className="h-9 w-9 flex-none object-contain"
          />
          <div className="max-w-[88%] rounded-2xl rounded-bl-md border border-white/10 bg-white/[.07] px-4 py-3 text-sm leading-relaxed text-white/85 shadow-lg">
            Dafür passen Mallorca, Kreta oder die Algarve sehr gut. Mallorca
            bietet im Oktober meist angenehme Temperaturen, kurze Flugzeiten
            und viele Ausflugsmöglichkeiten.
          </div>
        </div>

        <div
          className={`flex justify-end transition-all duration-700 ${
            phase >= 4 ? "translate-y-0 opacity-100" : "translate-y-3 opacity-0"
          }`}
        >
          <div className="max-w-[88%] rounded-2xl rounded-br-md bg-[#35c8c8] px-4 py-3 text-sm font-medium leading-relaxed text-[#07101d] shadow-lg">
            Mallorca klingt gut. Wichtig wären uns ein ruhiger Ort, ein
            Direktflug und ein Hotel in Strandnähe.
          </div>
        </div>

        {phase >= 5 && phase < 6 && <TypingBubble />}

        <div
          className={`flex items-end gap-3 transition-all duration-700 ${
            phase >= 6 ? "translate-y-0 opacity-100" : "translate-y-3 opacity-0"
          }`}
        >
          <img width="192" height="288" decoding="async"
            src="/assets/lyo.perf-511a1be44980.webp"
            alt=""
            className="h-9 w-9 flex-none object-contain"
          />
          <div className="max-w-[88%] rounded-2xl rounded-bl-md border border-white/10 bg-white/[.07] px-4 py-3 text-sm leading-relaxed text-white/85 shadow-lg">
            Dann würde ich euch Playa de Muro oder Alcúdia empfehlen. Beide
            bieten lange Strände, ruhigere Hotels und gute Ausflugsmöglichkeiten.
            Ich kann daraus jetzt euren persönlichen Reiseplan erstellen.
          </div>
        </div>

        <div
          className={`pt-2 text-center transition-all duration-700 ${
            phase >= 7 ? "translate-y-0 opacity-100" : "translate-y-2 opacity-0"
          }`}
        >
          <Link
            to="/registrieren"
            className="inline-flex items-center justify-center gap-2 rounded-full bg-[#35c8c8] px-6 py-3 text-sm font-extrabold text-[#07101d] transition hover:bg-[#54d5d5]"
          >
            Mit Lyo persönlich planen
            <ArrowRight size={17} weight="bold" />
          </Link>
          <p className="mt-3 text-xs text-white/45">
            Persönliche Planung nach kostenloser Registrierung
          </p>
        </div>
      </div>

      <style>{`
        @keyframes lyoTyping {
          0%, 60%, 100% { transform: translateY(0); opacity: .45; }
          30% { transform: translateY(-4px); opacity: 1; }
        }
      `}</style>
    </div>
  );
}

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-[#07101d] text-white">
      <UnifiedHeader />

      <main>
        <section className="relative isolate overflow-hidden">
          <img width="1774" height="887" decoding="async" loading="eager" fetchpriority="high"
            src="/assets/voyalyo-hero-travel-group-v2.perf-0adbb5b7922c.webp"
            alt=""
            className="absolute inset-0 -z-20 h-full w-full object-cover object-[64%_center] sm:object-[60%_center] lg:object-center"
          />
          <div
            className="absolute inset-0 -z-10"
            style={{
              background:
                "linear-gradient(90deg,rgba(6,35,57,.72) 0%,rgba(6,35,57,.50) 44%,rgba(6,35,57,.14) 72%,rgba(6,35,57,.05) 100%),linear-gradient(180deg,rgba(7,46,68,.03),rgba(7,46,68,.38))",
            }}
          />

          <div className="mx-auto grid min-h-[720px] max-w-7xl items-center gap-12 px-5 py-20 sm:px-8 lg:grid-cols-[1.02fr_.98fr] lg:py-28">
            <div className="max-w-2xl">
              <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-[#35c8c8]/35 bg-[#35c8c8]/10 px-4 py-2 text-sm font-bold text-[#70e0df]">
                <Sparkle size={17} weight="fill" />
                Reisen beginnen mit einer guten Idee
              </div>

              <h1 className="font-heading text-5xl font-extrabold leading-[1.05] tracking-tight sm:text-6xl lg:text-[68px]">
                Finde Menschen,
                <br />
                die <span className="text-[#43d1d0]">so reisen</span>
                <br />
                wie du.
              </h1>

              <p className="mt-7 max-w-xl text-lg leading-relaxed text-white/78 sm:text-xl">
                Suche passende Reisebegleitung nach Ziel, Zeitraum, Interessen
                und Reisestil. Lyo hilft dir zusätzlich bei der persönlichen
                Planung deiner Reise.
              </p>

              <div className="mt-9 flex flex-col gap-3 sm:flex-row">
                <a
                  href="#reisepartner"
                  className="inline-flex h-13 items-center justify-center gap-2 rounded-full bg-[#35c8c8] px-7 py-3.5 text-base font-extrabold text-[#07101d] transition hover:bg-[#54d5d5]"
                >
                  Reisebegleitung finden
                  <ArrowRight size={19} weight="bold" />
                </a>
                <Link
                  to="/registrieren"
                  className="inline-flex h-13 items-center justify-center rounded-full border border-white/25 bg-white/10 px-7 py-3.5 text-base font-bold text-white backdrop-blur transition hover:bg-white/15"
                >
                  Kostenlos registrieren
                </Link>
              </div>

              <div className="mt-7" data-testid="landing-store-badges">
                <p className="mb-3 text-sm font-medium text-white/80">
                  Voyalyo als App herunterladen
                </p>
                <div className="flex flex-wrap items-center gap-3">
                  <a href={STORE_LINKS.appStore} target="_blank" rel="noopener noreferrer" aria-label="Voyalyo im App Store laden" className="inline-flex rounded-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white">
                    <img src={appStoreBadge} alt="Laden im App Store" className="h-11 w-auto" />
                  </a>
                  <a href={STORE_LINKS.googlePlay} target="_blank" rel="noopener noreferrer" aria-label="Voyalyo bei Google Play herunterladen" className="inline-flex rounded-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white">
                    <img src={googlePlayBadge} alt="Jetzt bei Google Play" className="h-11 w-auto" />
                  </a>
                </div>
              </div>

              <div className="mt-8 flex flex-wrap gap-x-6 gap-y-3 text-sm text-white/70">
                {[
                  "Öffentliche Inspiration",
                  "Geschützte Kontakte",
                  "Ab 16 Jahren",
                ].map((item) => (
                  <span key={item} className="flex items-center gap-2">
                    <CheckCircle
                      size={18}
                      weight="fill"
                      className="text-[#43d1d0]"
                    />
                    {item}
                  </span>
                ))}
              </div>
            </div>

            <div className="relative mx-auto w-full max-w-xl">
              <LyoDemoChat />
            </div>
          </div>
        </section>

        <section id="reisepartner" className="bg-white px-5 py-24 text-[#0b1727] sm:px-8">
          <div className="mx-auto max-w-7xl">
            <div className="grid gap-12 lg:grid-cols-[.82fr_1.18fr] lg:items-start">
              <div className="lg:sticky lg:top-28">
                <div className="inline-flex items-center gap-2 rounded-full bg-cyan-50 px-4 py-2 text-sm font-extrabold text-[#087b82]">
                  <UsersThree size={19} weight="fill" />
                  Der Kern von Voyalyo
                </div>

                <h2 className="mt-5 font-heading text-4xl font-extrabold tracking-tight sm:text-5xl">
                  Reisebegleitung suchen und finden
                </h2>

                <p className="mt-5 text-lg leading-relaxed text-slate-600">
                  Nicht jeder möchte allein verreisen. Voyalyo bringt Menschen
                  zusammen, deren Reisepläne, Interessen und Reisestile
                  zueinander passen.
                </p>

                <ul className="mt-7 space-y-4">
                  {[
                    "Suche nach Reiseziel und Reisezeitraum",
                    "Vergleiche Interessen und Reisestile",
                    "Entscheide selbst, wen du kennenlernen möchtest",
                    "Persönliche Daten bleiben geschützt",
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-3 text-slate-700">
                      <CheckCircle
                        size={22}
                        weight="fill"
                        className="mt-0.5 flex-none text-[#168c91]"
                      />
                      {item}
                    </li>
                  ))}
                </ul>

                <Link
                  to="/registrieren"
                  className="mt-9 inline-flex items-center gap-2 rounded-full bg-[#0b1727] px-7 py-3.5 font-extrabold text-white transition hover:bg-[#14263d]"
                >
                  Passende Reisebegleitung finden
                  <ArrowRight size={18} weight="bold" />
                </Link>
              </div>

              <div className="rounded-[32px] border border-slate-200 bg-[#f4f8fb] p-5 shadow-sm sm:p-7">
                <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
                  <div>
                    <p className="font-heading text-xl font-extrabold">
                      Wonach suchst du?
                    </p>
                    <p className="mt-1 text-sm text-slate-500">
                      Vorschau der Voyalyo-Reisepartnersuche
                    </p>
                  </div>
                  <span className="w-fit rounded-full bg-white px-3 py-1 text-xs font-extrabold uppercase tracking-wider text-[#168c91] shadow-sm">
                    Vorschau
                  </span>
                </div>

                <div className="mt-6 grid gap-3 sm:grid-cols-3">
                  <div className="rounded-2xl border border-slate-200 bg-white p-4">
                    <p className="text-xs font-bold uppercase tracking-wider text-slate-400">
                      Reiseziel
                    </p>
                    <p className="mt-1 font-bold">Noch offen</p>
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-white p-4">
                    <p className="text-xs font-bold uppercase tracking-wider text-slate-400">
                      Reisezeit
                    </p>
                    <p className="mt-1 font-bold">Flexibel</p>
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-white p-4">
                    <p className="text-xs font-bold uppercase tracking-wider text-slate-400">
                      Reisestil
                    </p>
                    <p className="mt-1 font-bold">Entdecken</p>
                  </div>
                </div>

                <div className="mt-5 flex items-center justify-between">
                  <p className="text-sm font-semibold text-slate-500">
                    Beispiele für mögliche Reisegesuche
                  </p>
                  <span className="text-sm font-extrabold text-[#087b82]">
                    3 Vorschläge
                  </span>
                </div>

                <div className="mt-4 space-y-4">
                  <article className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <p className="text-xs font-extrabold uppercase tracking-wider text-[#168c91]">
                          Beispiel-Reisegesuch
                        </p>
                        <h3 className="mt-1 font-heading text-xl font-extrabold">
                          Mallorca im Oktober
                        </h3>
                        <p className="mt-1 text-sm text-slate-500">
                          7 bis 10 Tage · Abflug Norddeutschland
                        </p>
                      </div>
                      <span className="rounded-full bg-cyan-50 px-3 py-1 text-xs font-bold text-[#087b82]">
                        Entspannt
                      </span>
                    </div>
                    <div className="mt-4 flex flex-wrap gap-2">
                      {["Strand", "Ausflüge", "Gutes Essen", "Kein Partyurlaub"].map((tag) => (
                        <span key={tag} className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </article>

                  <article className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <p className="text-xs font-extrabold uppercase tracking-wider text-[#168c91]">
                          Beispiel-Reisegesuch
                        </p>
                        <h3 className="mt-1 font-heading text-xl font-extrabold">
                          Japan im Frühling
                        </h3>
                        <p className="mt-1 text-sm text-slate-500">
                          14 bis 18 Tage · Rundreise
                        </p>
                      </div>
                      <span className="rounded-full bg-violet-50 px-3 py-1 text-xs font-bold text-violet-700">
                        Kultur
                      </span>
                    </div>
                    <div className="mt-4 flex flex-wrap gap-2">
                      {["Tokio", "Kyoto", "Kulinarik", "Fotografie"].map((tag) => (
                        <span key={tag} className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </article>

                  <article className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <p className="text-xs font-extrabold uppercase tracking-wider text-[#168c91]">
                          Beispiel-Reisegesuch
                        </p>
                        <h3 className="mt-1 font-heading text-xl font-extrabold">
                          Nordlichter in Norwegen
                        </h3>
                        <p className="mt-1 text-sm text-slate-500">
                          6 bis 8 Tage · Winterreise
                        </p>
                      </div>
                      <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-bold text-emerald-700">
                        Natur
                      </span>
                    </div>
                    <div className="mt-4 flex flex-wrap gap-2">
                      {["Nordlichter", "Husky-Tour", "Natur", "Fotografie"].map((tag) => (
                        <span key={tag} className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </article>
                </div>

                <div className="mt-5 rounded-2xl border border-[#35c8c8]/25 bg-cyan-50 p-4 text-sm leading-relaxed text-slate-600">
                  Dies sind bewusst keine echten Nutzerprofile. Persönliche
                  Reisegesuche und Kontaktdaten werden erst nach Anmeldung und
                  unter den Voyalyo-Schutzregeln angezeigt.
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="moeglichkeiten" className="bg-[#f4f8fb] px-5 py-24 text-[#0b1727] sm:px-8">
          <div className="mx-auto max-w-7xl">
            <div className="max-w-3xl">
              <p className="text-sm font-extrabold uppercase tracking-[0.18em] text-[#168c91]">
                Erst entdecken, dann entscheiden
              </p>
              <h2 className="mt-3 font-heading text-4xl font-extrabold tracking-tight sm:text-5xl">
                Erlebe zuerst, was Voyalyo dir bietet
              </h2>
              <p className="mt-5 text-lg leading-relaxed text-slate-600">
                Du musst kein Konto anlegen, um Voyalyo kennenzulernen.
                Persönliche Profile, Kontakte und Nachrichten bleiben weiterhin geschützt.
              </p>
            </div>

            <div className="mt-12 grid gap-6 lg:grid-cols-3">
              {FEATURES.map((feature) => {
                const Icon = feature.icon;
                return (
                  <article
                    key={feature.title}
                    className="flex flex-col rounded-3xl border border-slate-200 bg-white p-7 shadow-sm transition hover:-translate-y-1 hover:shadow-xl"
                  >
                    <span className="flex h-13 w-13 items-center justify-center rounded-2xl bg-cyan-50 text-[#168c91]">
                      <Icon size={27} weight="fill" />
                    </span>
                    <p className="mt-6 text-sm font-extrabold uppercase tracking-wider text-[#168c91]">
                      {feature.label}
                    </p>
                    <h3 className="mt-2 font-heading text-2xl font-extrabold">
                      {feature.title}
                    </h3>
                    <p className="mt-4 flex-1 leading-relaxed text-slate-600">
                      {feature.text}
                    </p>
                    <a
                      href={feature.href}
                      className="mt-7 inline-flex items-center gap-2 font-bold text-[#087b82] hover:text-[#055c62]"
                    >
                      {feature.action}
                      <ArrowRight size={17} weight="bold" />
                    </a>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        <section id="reiseideen" className="bg-white px-5 py-24 text-[#0b1727] sm:px-8">
          <div className="mx-auto max-w-7xl">
            <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
              <div className="max-w-3xl">
                <p className="text-sm font-extrabold uppercase tracking-[0.18em] text-[#168c91]">
                  Reiseinspiration
                </p>
                <h2 className="mt-3 font-heading text-4xl font-extrabold tracking-tight sm:text-5xl">
                  Vielleicht beginnt deine nächste Reise hier
                </h2>
              </div>
              <a
                href="/reiseideen"
                className="inline-flex items-center gap-2 font-bold text-[#087b82]"
              >
                Alle Reiseideen ansehen
                <ArrowRight size={18} weight="bold" />
              </a>
            </div>

            <div className="mt-12 grid gap-6 lg:grid-cols-3">
              {IDEAS.map((idea) => (
                <a
                  key={idea.title}
                  href="/reiseideen"
                  className="group relative min-h-[360px] overflow-hidden rounded-3xl bg-slate-900 shadow-lg"
                >
                  <img loading="lazy" decoding="async"
                    src={idea.image}
                    alt=""
                    className="absolute inset-0 h-full w-full object-cover transition duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/25 to-transparent" />
                  <div className="absolute inset-x-0 bottom-0 p-7 text-white">
                    <p className="text-sm font-bold text-[#70e0df]">
                      Voyalyo Reiseidee
                    </p>
                    <h3 className="mt-2 font-heading text-2xl font-extrabold">
                      {idea.title}
                    </h3>
                    <p className="mt-2 text-white/75">{idea.text}</p>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-[#0b1727] px-5 py-24 sm:px-8">
          <div className="mx-auto max-w-7xl">
            <div className="grid gap-12 lg:grid-cols-[.85fr_1.15fr] lg:items-center">
              <div>
                <p className="text-sm font-extrabold uppercase tracking-[0.18em] text-[#43d1d0]">
                  So funktioniert Voyalyo
                </p>
                <h2 className="mt-3 font-heading text-4xl font-extrabold tracking-tight sm:text-5xl">
                  Vom ersten Gedanken bis zur gemeinsamen Reise
                </h2>
                <p className="mt-5 text-lg leading-relaxed text-white/65">
                  Öffentliche Inspiration steht am Anfang. Geschützte Funktionen
                  werden erst dann relevant, wenn du aktiv teilnehmen möchtest.
                </p>
              </div>

              <div className="space-y-4">
                {STEPS.map((step) => (
                  <div
                    key={step.number}
                    className="grid gap-4 rounded-2xl border border-white/10 bg-white/[.05] p-5 sm:grid-cols-[64px_1fr] sm:items-center"
                  >
                    <div className="font-heading text-3xl font-extrabold text-[#43d1d0]">
                      {step.number}
                    </div>
                    <div>
                      <h3 className="font-heading text-xl font-bold">{step.title}</h3>
                      <p className="mt-1 text-white/65">{step.text}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section id="sicherheit" className="bg-[#eef7f7] px-5 py-24 text-[#0b1727] sm:px-8">
          <div className="mx-auto grid max-w-7xl gap-12 lg:grid-cols-2 lg:items-center">
            <div className="rounded-[34px] bg-[#0b1727] p-8 text-white shadow-xl sm:p-10">
              <ShieldCheck size={54} weight="fill" className="text-[#43d1d0]" />
              <h2 className="mt-6 font-heading text-4xl font-extrabold">
                Offen entdecken. Geschützt verbinden.
              </h2>
              <p className="mt-5 text-lg leading-relaxed text-white/70">
                Reiseideen und Erklärungen sind öffentlich. Persönliche Profile,
                Matches, Nachrichten und Kontaktdaten bleiben ausschließlich
                angemeldeten und entsprechend geprüften Nutzern vorbehalten.
              </p>
            </div>

            <div>
              <p className="text-sm font-extrabold uppercase tracking-[0.18em] text-[#168c91]">
                Sicherheit ist Teil des Konzepts
              </p>
              <ul className="mt-7 space-y-5">
                {[
                  "Keine öffentlichen Kontaktdaten oder privaten Nachrichten",
                  "Alters- und Verifizierungsprüfungen für geschützte Funktionen",
                  "Melde- und Blockiermöglichkeiten innerhalb der Community",
                  "Du entscheidest selbst, wann du Kontakt aufnehmen möchtest",
                ].map((item) => (
                  <li key={item} className="flex gap-3 text-lg text-slate-700">
                    <CheckCircle
                      size={24}
                      weight="fill"
                      className="mt-0.5 flex-none text-[#168c91]"
                    />
                    {item}
                  </li>
                ))}
              </ul>
              <a
                href="/hilfe-sicherheit"
                className="mt-8 inline-flex items-center gap-2 font-bold text-[#087b82]"
              >
                Mehr über Sicherheit erfahren
                <ArrowRight size={18} weight="bold" />
              </a>
            </div>
          </div>
        </section>

        <section className="relative overflow-hidden bg-[#07101d] px-5 py-24 text-center sm:px-8">
          <div className="absolute inset-0 opacity-20">
            <img width="1774" height="887" decoding="async" loading="lazy"
              src="/assets/voyalyo-hero-travel-group-v2.perf-0adbb5b7922c.webp"
              alt=""
              className="h-full w-full object-cover"
            />
          </div>
          <div className="relative mx-auto max-w-3xl">
            <h2 className="font-heading text-4xl font-extrabold sm:text-5xl">
              Bereit für deine nächste Reise?
            </h2>
            <p className="mx-auto mt-5 max-w-2xl text-lg text-white/70">
              Entdecke Voyalyo in Ruhe und erstelle dein Konto, wenn du Lyo,
              Reisepartnersuche und Community-Funktionen nutzen möchtest.
            </p>
            <div className="mt-9 flex flex-col justify-center gap-3 sm:flex-row">
              <Link
                to="/registrieren"
                className="rounded-full bg-[#35c8c8] px-8 py-3.5 font-extrabold text-[#07101d] transition hover:bg-[#54d5d5]"
              >
                Kostenlos starten
              </Link>
              <Link
                to="/login"
                className="rounded-full border border-white/25 bg-white/10 px-8 py-3.5 font-bold text-white transition hover:bg-white/15"
              >
                Bereits registriert? Anmelden
              </Link>
            </div>
          </div>
        </section>

        <section id="bekannt-aus" aria-labelledby="bekannt-aus-title"
          className="border-t border-white/10 bg-[#0b1727] px-5 py-12 text-white sm:px-8">
          <div className="mx-auto flex max-w-7xl flex-col items-center gap-6 text-center sm:flex-row sm:justify-between sm:text-left">
            <div>
              <h2 id="bekannt-aus-title" className="font-heading text-2xl font-bold">
                Bekannt aus
              </h2>
              <p className="mt-2 max-w-xl text-base text-white/75">
                StartupValley stellt Voyalyo vor: Reiseplanung mit Lyo,
                passende Reisepartner und die Geschichte hinter der Plattform.
              </p>
              <a href="https://startupvalley.news/de/voyalyo-reiseplanung-reisepartner/"
                target="_blank" rel="noopener noreferrer"
                className="mt-4 inline-block font-bold text-[#70e0df] underline underline-offset-4">
                Interview bei StartupValley lesen
              </a>
            </div>
            <a href="https://startupvalley.news/de/voyalyo-reiseplanung-reisepartner/"
              target="_blank" rel="noopener noreferrer"
              aria-label="Voyalyo-Interview bei StartupValley öffnen"
              className="block shrink-0 rounded-xl bg-white p-4 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4">
              <img src="/assets/StartupValley-Logo.png"
                alt="StartupValley"
                width="320" height="152"
                loading="lazy" decoding="async"
                className="block h-auto w-56 max-w-full" />
            </a>
          </div>
        </section>

      </main>

      <footer className="border-t border-white/10 bg-[#050b14] px-5 py-10 text-sm text-white/55 sm:px-8">
        <div className="mx-auto flex max-w-7xl flex-col gap-7 md:flex-row md:items-center md:justify-between">
          <div>
            <Logo heightClass="h-8" />
            <p className="mt-3">Reisen planen. Menschen treffen. Gemeinsam erleben.</p>
          </div>
          <div className="flex flex-wrap gap-x-6 gap-y-3">
            <a href="/rechtliches/impressum" className="hover:text-white">Impressum</a>
            <a href="/rechtliches/datenschutz" className="hover:text-white">Datenschutz</a>
            <a href="/rechtliches/nutzungsbedingungen" className="hover:text-white">Nutzungsbedingungen</a>
            <a href="/rechtliches/support" className="hover:text-white">Kontakt & Support</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
